import { componentProvider } from 'js-component-framework/es';

import getInteractiveChildren from '@/js/utils/getInteractiveChildren';
import getFirstAndLastItems from '@/js/utils/getFirstAndLastItems';
import breakpoints from '@/js/utils/breakpoints';

/**
 * MenuButton component.
 */
export default class MenuButton {
  /**
   * Initial expanded state.
   * @private
   *
   * @type {Boolean}
   */
  #expandedState = false;

  /**
   * Gets the target element based on the controller's `aria-controls` attribute.
   *
   * @param  {HTMLButtonElement} controller The component's controlling element.
   * @return {HTMLElement|null}
   */
  static getTargetElement(controller) {
    if (!controller.hasAttribute('aria-controls')) {
      const message = 'The component element is missing the required \'aria-controls\' attribute';
      throw new Error(`Configuration error: ${message}`);
    }

    const targetId = controller.getAttribute('aria-controls');
    const target = document.getElementById(targetId);

    if (target === null) {
      const message = `A target element with ID of '${targetId}' is not found`;
      throw new Error(`Configuration error: ${message}`);
    }

    return target;
  }

  /**
   * Starts the component
   *
   * @param  {HTMLElement} options.element The component element.
   * @param  {object}      options.options The options object.
   */
  constructor({ element, options }) {
    // Collect elements.
    this.controller = element;
    this.target = this.constructor.getTargetElement(element);

    /**
     * Whether the component has been initialized.
     *
     * @type {Boolean}
     */
    this.initialized = false;

    /**
     * Component configuration options.
     *
     * @type {object}
     */
    const { close, useTransition } = {
      /**
       * Whether the CSS uses a transition, which will impact the ability to
       * focus elements within the target element.
       *
       * @type {boolean}
       */
      useTransition: false,

      /**
       * The selector for the target's close button.
       *
       * @type {string}
       */
      close: '',

      // Merge options with defaults.
      ...options,
    };

    this.useTransition = useTransition;

    if (close !== '') {
      /**
       * The element used to hide the target.
       *
       * @type {HTMLButtonElement}
       */
      this.closeButton = document.querySelector(close);
    }

    breakpoints.lgMin.addEventListener('change', this.init);
    this.init();
  }

  /**
   * Conditionally initialize the MenuButton.
   */
  init = () => {
    if (breakpoints.lgMin.matches) {
      if (this.initialized) {
        // Destroy for large screens.
        this.destroy();
      }
    } else {
      this.setUp();
    }
  };

  /**
   * Sets up the component's DOM attributes and event listeners.
   */
  setUp = () => {
    /*
     * Set the target as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    this.target.setAttribute('aria-hidden', 'true');
    this.target.setAttribute('role', 'dialog');
    this.target.setAttribute('aria-modal', 'true');

    // Add event listeners
    this.controller.addEventListener('click', this.show);
    this.target.addEventListener('keydown', this.handleTargetKeydown);

    if (this.closeButton != null) {
      this.closeButton.addEventListener('click', this.handleCloseButtonClick);
      this.closeButton.addEventListener('keydown', this.handleCloseButtonKeydown);
    }

    if (this.useTransition) {
      this.target.addEventListener('transitionend', this.handleTargetShown);
    }

    this.initialized = true;
  };

  /**
   * Sets expanded state.
   *
   * @param {bool} newState The expected expanded state.
   */
  set expanded(newState) {
    this.#expandedState = newState;

    requestAnimationFrame(() => {
      this.controller.setAttribute('aria-expanded', this.expanded);
    });

    // Update target attributes and activeElement.
    if (this.expanded) {
      requestAnimationFrame(() => {
        this.target.setAttribute('aria-hidden', 'false');
      });

      if (!this.useTransition) {
        this.closeButton.focus();
      }
    } else {
      requestAnimationFrame(() => {
        this.target.setAttribute('aria-hidden', 'true');
      });
    }

    /*
     * Collect first and last interactive child elements from the target element
     * and merge them in as instance properties.
     *
     * Hidden elements are excluded, so we do this (once) after the expanded
     * state changes.
     */
    if (!this.firstInteractiveChild || !this.lastInteractiveChild) {
      // Collect the target element's interactive child elements.
      const interactiveChildElements = getInteractiveChildren(this.target);

      if (interactiveChildElements.length > 0) {
        const [
          firstInteractiveChild,
          lastInteractiveChild,
        ] = getFirstAndLastItems(interactiveChildElements);

        this.firstInteractiveChild = firstInteractiveChild;
        this.lastInteractiveChild = lastInteractiveChild;
      }
    }
  }

  /**
   * Returns expanded state.
   *
   * @return {bool} The current expanded state.
   */
  get expanded() {
    return this.#expandedState;
  }

  /**
   * Handles keydown events on the target.
   *
   * @param {Event} event The Event object.
   */
  handleTargetKeydown = (event) => {
    const { code, shiftKey } = event;
    const { activeElement } = document;

    if (code === 'Escape' && this.expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      requestAnimationFrame(() => {
        this.hide();
      });
    } else if (code === 'Tab') {
      // Trap tabs within the modal.
      // The close button is outside the modal, so we have to patch it in.
      if (shiftKey) {
        if (this.firstInteractiveChild === activeElement) {
          event.preventDefault();

          if (this.closeButton) {
            this.closeButton.focus();
          } else {
            this.lastInteractiveChild.focus();
          }
        }
      } else if (this.lastInteractiveChild === activeElement) {
        event.preventDefault();

        if (this.closeButton) {
          this.closeButton.focus();
        } else {
          this.firstInteractiveChild.focus();
        }
      }
    }
  };

  /**
   * Handles click events on the close button.
   *
   * @param {Event} event The Event object.
   */
  handleCloseButtonClick = (event) => {
    if (this.expanded) {
      event.preventDefault();

      requestAnimationFrame(() => {
        this.hide();
      });
    }
  };

  /**
   * Handles close button keydown events.
   *
   * @param {Event} event The Event object.
   */
  handleCloseButtonKeydown = (event) => {
    const { code, shiftKey } = event;

    if (code === 'Escape' && this.expanded) {
      event.preventDefault();

      // Hide the target when the Escape key is pressed.
      requestAnimationFrame(() => {
        this.hide();
      });
    } else if (code === 'Tab' && this.expanded) {
      event.preventDefault();

      // Includes the close button in trapped tabs.
      if (shiftKey) {
        this.lastInteractiveChild.focus();
      } else {
        this.firstInteractiveChild.focus();
      }
    }
  };

  /**
   * Handles visible target after a CSS transition.
   */
  handleTargetShown = (event) => {
    // Bail early if the transition is on a child element.
    if (this.target !== event.target) {
      return;
    }

    this.closeButton.focus();
  };

  /**
   * Updates component state to show the target element.
   */
  show = () => {
    this.expanded = true;
  };

  /**
   * Updates component state to hide the target element.
   */
  hide = () => {
    this.expanded = false;

    // Move focus to the controller.
    this.controller.focus();
  };

  /**
   * Removes all attributes and event listeners added by this class.
   */
  destroy = () => {
    // Remove controller attributes.
    this.controller.removeAttribute('aria-expanded');

    // Remove target attributes.
    this.target.removeAttribute('aria-hidden');
    this.target.removeAttribute('role');
    this.target.removeAttribute('aria-modal');

    // Remove event listeners.
    this.controller.removeEventListener('click', this.show);
    this.target.removeEventListener('keydown', this.handleTargetKeydown);

    if (this.closeButton != null) {
      this.closeButton.removeEventListener('click', this.handleCloseButtonClick);
      this.closeButton.removeEventListener('keydown', this.handleCloseButtonKeydown);
    }

    if (this.useTransition) {
      this.target.removeEventListener('transitionend', this.handleTargetShown);
    }

    // Reset initial state by setting the private field so the setter doesn't run.
    this.#expandedState = false;
  };
}

const menuButtonConfig = {
  name: 'menu-button',
  component: MenuButton,
  options: {
    close: '.site-header__menu-close',
  },
};

componentProvider(menuButtonConfig);
